import { Ray } from "@babylonjs/core/Culling/ray.js";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { WebXRHandJoint } from "@babylonjs/core/XR/features/WebXRHandTracking.js";
import { CompatibilityOptionsImpl } from "../settings/CompatibilityOptions";
import { WebXRFeatureName } from "@babylonjs/core/XR/webXRFeaturesManager.js";
import { DcRenderingGroup } from "./DcRenderingGroup";
import { WebXRControllerPointerSelection } from "@babylonjs/core/XR/features/WebXRControllerPointerSelection.js";
import { Mesh2D } from "@dreamcam/babylon-tools";
const HAND_PROFILES = new Set([
    "oculus-hand",
    "generic-hand",
    "generic-hand-select",
]);
export class CompatibilityModule {
    get xrSessionInit() {
        return { optionalFeatures: ["hand-tracking"] };
    }
    get controllers() {
        //@ts-expect-error
        return this._xrHelper.pointerSelection._controllers;
    }
    constructor(scene, xrHelper) {
        this._handTracking = null;
        this._selectActive = false;
        this._hasBuiltInSelectEvent = false;
        this._hasPicked = false;
        this._activeController = null;
        this._pick = null;
        this._pointerId = 0;
        this._xrSessionSelectStart = (e) => {
            this._hasBuiltInSelectEvent = true;
            if (e.inputSource.targetRayMode === "transient-pointer") {
                this._selectActive = true;
                this._hasPicked = false;
                this._activeController = e.inputSource.handedness;
            }
        };
        this._xrSessionSelectEnd = (e) => {
            if (e.inputSource.targetRayMode === "transient-pointer") {
                this._selectActive = false;
                this._activeController = null;
            }
        };
        this._xrFrame = () => {
            const controllerList = this._compatibilityOptions?.visionPro?.enableGazeInput !== false && !this._xrHelper.input.controllers.some(c => c.inputSource.targetRayMode === "tracked-pointer") ? ["camera"] : this._xrHelper.input.controllers.map(c => c.uniqueId);
            for (const controllerId of controllerList) {
                const controllerData = this.controllers[controllerId];
                if (!this._handTrackingEnabled && this._isHandProfile(controllerData?.xrController?.inputSource?.profiles)) {
                    continue;
                }
                this._pick = controllerData?.pick ?? null;
                if (this._xrHelper?.pointerSelection.attached === true) {
                    this._pointerId = controllerData?.id ?? 0;
                }
                if (this._handTrackingEnabled && this._compatibilityOptions?.visionPro?.customGestureHandler?.enabled !== false && !this._hasBuiltInSelectEvent && this._handTracking?.attached === true) {
                    const pinchDistance = this._compatibilityOptions?.visionPro?.customGestureHandler?.pinchDistance ?? 0.005;
                    const releaseDistance = this._compatibilityOptions?.visionPro?.customGestureHandler?.releaseDistance ?? 0.01;
                    const hand = this._handTracking?.getHandByControllerId(controllerId);
                    if (hand === null || hand === undefined)
                        continue;
                    const thumbTip = hand.getJointMesh(WebXRHandJoint.THUMB_TIP);
                    const indexTip = hand.getJointMesh(WebXRHandJoint.INDEX_FINGER_TIP);
                    if (!this._selectActive && this._activeController === null && thumbTip.position.subtract(indexTip.position).length() <= pinchDistance) {
                        this._selectActive = true;
                        this._hasPicked = false;
                        this._activeController = hand.xrController.inputSource.handedness;
                        break;
                    }
                    else if (this._selectActive && this._activeController === hand.xrController.inputSource.handedness && thumbTip.position.subtract(indexTip.position).length() > releaseDistance) {
                        this._selectActive = false;
                        this._activeController = null;
                        break;
                    }
                }
            }
            if (this._pick !== null) {
                if (this._selectActive && !this._hasPicked) {
                    this._scene.simulatePointerDown(this._pick, {
                        pointerId: this._pointerId,
                        pointerType: "xr",
                    });
                    this._hasPicked = true;
                }
                else if (!this._selectActive && this._hasPicked) {
                    if (this._pick.pickedMesh?.material) {
                        const tex = this._pick.pickedMesh.material.getActiveTextures()[0];
                        if (tex && tex._lastControlOver) {
                            let control = tex._lastControlOver[this._pointerId];
                            while (control) {
                                if ("onBlurObservable" in control) {
                                    const input = control;
                                    const observers = input.onBlurObservable.observers;
                                    for (const o of observers)
                                        input.onBlurObservable.remove(o);
                                    setTimeout(() => {
                                        for (const o of observers)
                                            input.onBlurObservable.add(o.callback, o.mask);
                                    }, 0);
                                }
                                control = control.parent;
                            }
                        }
                    }
                    this._scene.simulatePointerUp(this._pick, {
                        pointerId: this._pointerId,
                        pointerType: "xr",
                    });
                    if (this._compatibilityOptions?.visionPro?.enableGazeInput === false && this._pick.pickedMesh?.material) {
                        const tex = this._pick.pickedMesh.material.getActiveTextures()[0];
                        if (tex && tex._lastControlOver) {
                            let control = tex._lastControlOver[this._pointerId];
                            while (control) {
                                control.onPointerOutObservable?.notifyObservers(control);
                                control._onPointerOut(control, null, true);
                                control = control.parent;
                            }
                        }
                    }
                    this._hasPicked = false;
                    this._pick = null;
                    this._pointerId = 0;
                }
                else {
                    this._scene.simulatePointerMove(this._pick, {
                        pointerId: this._pointerId,
                        pointerType: "xr",
                    });
                }
            }
        };
        this._xrSessionInit = (session) => {
            if (this._compatibilityOptions?.visionPro?.customTransientPointerHandler !== false) {
                session.addEventListener('selectstart', this._xrSessionSelectStart);
                session.addEventListener('selectend', this._xrSessionSelectEnd);
            }
        };
        this._xrSessionEnded = (session) => {
            if (this._compatibilityOptions?.visionPro?.customTransientPointerHandler !== false) {
                session?.removeEventListener('selectstart', this._xrSessionSelectStart);
                session?.removeEventListener('selectend', this._xrSessionSelectEnd);
            }
        };
        this._scene = scene;
        this._xrHelper = xrHelper;
        this._compatibilityOptions = CompatibilityOptionsImpl.getInstance().data;
        this._handTrackingEnabled = this._compatibilityOptions?.enableHandTracking !== false;
        console.log("CompatibilityModule: handTrackingEnabled=", this._handTrackingEnabled);
        this._handTracking = this._xrHelper.baseExperience.featuresManager.enableFeature(WebXRFeatureName.HAND_TRACKING, "latest", { xrInput: this._xrHelper.input }, true, false);
        this._handTracking?.onHandAddedObservable.add(hand => {
            if (!hand?.handMesh)
                return;
            if (this._handTrackingEnabled) {
                console.log("CompatibilityModule: hand mesh added (enabled)", hand.handMesh.name);
                hand.handMesh.renderingGroupId = DcRenderingGroup.pointerAndLaser;
            }
            else {
                console.log("CompatibilityModule: hand mesh added (disabled)", hand.handMesh.name);
                if (hand.xrController?.pointer) {
                    hand.xrController.pointer.isVisible = false;
                    hand.xrController.pointer.setEnabled(false);
                }
                hand.handMesh.setEnabled(false);
                hand.handMesh.isVisible = false;
                hand.handMesh.isPickable = false;
                hand.handMesh.getChildMeshes(false).forEach((mesh) => {
                    mesh.setEnabled(false);
                    mesh.isVisible = false;
                    mesh.isPickable = false;
                });
            }
        });
        if (!this._handTrackingEnabled) {
            const featuresManager = this._xrHelper.baseExperience.featuresManager;
            featuresManager?.disableFeature?.(WebXRFeatureName.NEAR_INTERACTION);
        }
        if (this._compatibilityOptions?.visionPro?.customTransientPointerHandler !== false) {
            this._xrHelper.baseExperience.sessionManager.onXRSessionInit.add(this._xrSessionInit);
            this._xrHelper.baseExperience.sessionManager.onXRSessionEnded.add(this._xrSessionEnded);
        }
        if (this._compatibilityOptions?.visionPro?.customTransientPointerHandler !== false || this._compatibilityOptions?.visionPro?.customGestureHandler?.enabled !== false)
            this._xrHelper.baseExperience.sessionManager.onXRFrameObservable.add(this._xrFrame);
        if (this._compatibilityOptions?.visionPro?.enableGazeInput !== false) {
            this._addCameraController();
            this._xrHelper.input.onControllerAddedObservable.add(() => this._onControllerAdded());
            this._xrHelper.input.onControllerRemovedObservable.add(() => this._onControllerRemoved());
        }
        Mesh2D.p3ColorSpace = this._compatibilityOptions?.visionPro?.colorCorrection === true;
    }
    _onControllerAdded() {
        if (this._xrHelper.input.controllers.some(c => c.inputSource.targetRayMode === "tracked-pointer")) {
            const cameraController = this.controllers["camera"];
            if (cameraController) {
                delete this.controllers["camera"];
                cameraController.laserPointer.dispose();
                cameraController.selectionMesh.dispose();
            }
        }
    }
    _onControllerRemoved() {
        if (!this._xrHelper.input.controllers.some(c => c.inputSource.targetRayMode === "tracked-pointer") && !this.controllers["camera"]) {
            this._addCameraController();
        }
    }
    _isHandProfile(profiles) {
        if (!profiles)
            return false;
        return profiles.some(p => HAND_PROFILES.has(p));
    }
    _addCameraController() {
        const webXRCamera = this._xrHelper.baseExperience.camera;
        //@ts-expect-error
        const { laserPointer, selectionMesh } = this._xrHelper.pointerSelection._generateNewMeshPair(webXRCamera);
        this.controllers["camera"] = {
            webXRCamera,
            laserPointer,
            selectionMesh,
            meshUnderPointer: null,
            pick: null,
            tmpRay: new Ray(new Vector3(), new Vector3()),
            disabledByNearInteraction: false,
            //@ts-expect-error
            id: WebXRControllerPointerSelection._IdCounter++,
        };
    }
}
